interface DashboardMetricSparklineProps {
  label: string;
  values: readonly number[];
}

const width = 160;
const height = 32;
const verticalPadding = 3;

export function DashboardMetricSparkline({ label, values }: DashboardMetricSparklineProps) {
  const finiteValues = values.filter(Number.isFinite);
  if (finiteValues.length === 0) return null;

  const points = sparklinePoints(finiteValues);
  const linePath = points
    .map(([x, y], index) => [index === 0 ? 'M' : 'L', x, ',', y].join(''))
    .join(' ');
  const areaPath = [
    ['M', points[0]?.[0] ?? 0, ',', height].join(''),
    points.map(([x, y]) => ['L', x, ',', y].join('')).join(' '),
    ['L', points.at(-1)?.[0] ?? width, ',', height, ' Z'].join(''),
  ].join(' ');

  return (
    <svg
      aria-label={`${label} trend`}
      className="mt-auto h-8 w-full pt-3"
      preserveAspectRatio="none"
      role="img"
      viewBox={[0, 0, width, height].join(' ')}
    >
      <path data-sparkline-area="true" d={areaPath} fill="var(--chart-1)" opacity="0.12" />
      <path
        data-sparkline-line="true"
        d={linePath}
        fill="none"
        stroke="var(--chart-1)"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {points.length === 1 ? (
        <circle cx={points[0]?.[0]} cy={points[0]?.[1]} fill="var(--chart-1)" r="2" />
      ) : null}
    </svg>
  );
}

function sparklinePoints(values: readonly number[]): [number, number][] {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const valueRange = maximum - minimum;

  return values.map((value, index) => {
    const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
    const y =
      valueRange === 0
        ? height / 2
        : height -
          verticalPadding -
          ((value - minimum) / valueRange) * (height - verticalPadding * 2);
    return [round(x), round(y)];
  });
}

function round(value: number) {
  return Math.round(value * 100) / 100;
}
